import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Wrench, CalendarCheck } from 'lucide-react';
import { format } from 'date-fns';

/**
 * Blue Service Details block shown when cost_type === 'service'.
 * Confirms the entry, captures service interval, computes next-service reading,
 * and displays the estimated next-service date based on historical km/day.
 */
export const ServiceIntervalSection = ({
  formData,
  updateField,
  isHoursBased,
  serviceConfirmed,
  setServiceConfirmed,
  nextServiceOdo,
  setNextServiceOdo,
  expectedNextServiceDate,
  avgKmPerDay,
  allVehicleCosts,
  selectedVehicle,
}) => (
  <div className="bg-blue-50 border-2 border-blue-200 rounded-sm p-4 space-y-4" data-testid="service-interval-section">
    <h5 className="font-display font-black text-blue-950 flex items-center gap-2 text-base uppercase tracking-tight">
      <Wrench className="w-5 h-5" />
      Service Details
    </h5>

    {/* Confirmation checkbox */}
    <label className="flex items-start gap-3 cursor-pointer bg-white border-2 border-blue-300 rounded-sm p-3" data-testid="service-confirmation-label">
      <input
        type="checkbox"
        checked={serviceConfirmed}
        onChange={(e) => setServiceConfirmed(e.target.checked)}
        className="w-5 h-5 mt-0.5 accent-blue-700 cursor-pointer"
        data-testid="service-confirmation-checkbox"
      />
      <span className="text-sm font-bold text-blue-900 leading-snug">
        I confirm this is a <span className="uppercase">{formData.cost_type}</span> entry —
        vehicle service history will be updated.
      </span>
    </label>

    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-blue-900 text-xs">{isHoursBased ? 'Interval (hours)' : 'Interval (km)'}</Label>
        <Input
          type="number" step="1" min="1"
          value={formData.service_interval_km}
          onChange={(e) => {
            updateField('service_interval_km', e.target.value);
            const currentReading = parseFloat(isHoursBased ? formData.hours : formData.odometer);
            if (currentReading && e.target.value) {
              setNextServiceOdo(String(Math.round(currentReading + parseFloat(e.target.value))));
            }
          }}
          placeholder={isHoursBased ? '500' : '10000'}
          className="font-data border-blue-300 bg-white"
          data-testid="service-interval-input"
        />
        {isHoursBased && (
          <div className="flex flex-wrap gap-1 pt-1" data-testid="service-interval-hour-chips">
            {[150, 200, 250, 300, 350, 400, 450, 500].map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => {
                  updateField('service_interval_km', String(h));
                  const currentReading = parseFloat(formData.hours);
                  if (currentReading) setNextServiceOdo(String(Math.round(currentReading + h)));
                }}
                className={`px-2 py-0.5 rounded-full text-[11px] font-data border transition-colors ${String(formData.service_interval_km) === String(h) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-800 border-blue-300 hover:bg-blue-100'}`}
                data-testid={`service-interval-chip-${h}`}
              >
                {h}h
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-1">
        <Label className="text-blue-900 text-xs">
          Next Service {isHoursBased ? 'Hours' : 'Odometer'} *
        </Label>
        <Input
          type="number"
          step={isHoursBased ? '1' : '100'}
          min="0"
          value={nextServiceOdo}
          onChange={(e) => setNextServiceOdo(e.target.value)}
          placeholder={isHoursBased ? 'e.g. 1500' : 'e.g. 72000'}
          className="font-data border-blue-300 bg-white"
          data-testid="next-service-odo-input"
        />
      </div>
    </div>

    {/* Expected next-service date */}
    <div className="bg-white border-2 border-blue-300 rounded-sm p-3" data-testid="expected-service-date-box">
      <div className="flex items-center gap-2 mb-1">
        <CalendarCheck className="w-4 h-4 text-blue-700" />
        <span className="label-eyebrow text-blue-900">Expected Next Service Date</span>
      </div>
      {expectedNextServiceDate ? (
        <>
          <p className="font-display text-xl font-black text-blue-950 tabular-nums" data-testid="expected-service-date-value">
            {format(expectedNextServiceDate, 'EEEE, d MMMM yyyy')}
          </p>
          <p className="text-xs font-mono text-blue-700 mt-1">
            based on {avgKmPerDay ? avgKmPerDay.toFixed(1) : '—'} {isHoursBased ? 'hrs' : 'km'}/day (median of your recent readings, outliers ignored)
          </p>
        </>
      ) : (
        <p className="text-sm font-medium text-blue-700">
          {allVehicleCosts.length < 2
            ? 'Add more cost entries to estimate next service date'
            : `Enter ${isHoursBased ? 'hours' : 'odometer'} and next service reading to calculate`}
        </p>
      )}
    </div>

    {selectedVehicle?.last_service_odometer && !isHoursBased && (
      <p className="text-xs text-blue-700 font-mono">
        Last service: {selectedVehicle.last_service_odometer.toLocaleString()} km
        {selectedVehicle.last_service_date ? ` on ${selectedVehicle.last_service_date}` : ''}
        {formData.odometer ? ` — ${(parseFloat(formData.odometer) - selectedVehicle.last_service_odometer).toLocaleString()} km since` : ''}
      </p>
    )}
  </div>
);
